import { memo } from "react"
import { Handle, Position, type NodeProps } from "@xyflow/react"
import type { AlgorithmNode } from "../ConstructorProvider"
import { useEditableLabel } from "./useEditableLabel"
import EditableLabel from "./EditableLabel"

const InputNode = memo(({ id, data, selected }: NodeProps<AlgorithmNode>) => {
  const { editing, label, setLabel, startEditing, commit, onKeyDown } =
    useEditableLabel(id, data.label, { multiline: true })

  const borderColor = data.hasError
    ? "hsl(var(--node-error))"
    : selected
      ? "hsl(var(--ring))"
      : "transparent"

  return (
    <div style={{ position: "relative" }}>
      <svg
        width="20"
        height="16"
        viewBox="0 0 20 16"
        aria-hidden="true"
        style={{
          position: "absolute",
          left: "-6px",
          top: "20%",
          transform: "translateY(-50%)",
          pointerEvents: "none",
        }}
      >
        <path
          d="M2 8 L16 8 M11 3 L17 8 L11 13"
          fill="none"
          stroke="hsl(var(--node-input))"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>

      <div
        className="node-base"
        style={{
          background: "hsl(var(--node-input) / 0.12)",
          color: "hsl(var(--foreground))",
          border: `2.5px solid ${borderColor}`,
          padding: "12px 36px",
          // A fixed slant, smaller than the side padding: a percentage grew
          // with a long message until it cut into the text.
          clipPath: "polygon(24px 0%, 100% 0%, calc(100% - 24px) 100%, 0% 100%)",
          boxShadow: "none",
          minWidth: "160px",
          position: "relative",
        }}
        onDoubleClick={startEditing}
      >
        <Handle
          type="target"
          position={Position.Top}
          style={{ background: "hsl(var(--node-input))" }}
        />

        <EditableLabel
          editing={editing}
          value={label}
          onChange={setLabel}
          onBlur={commit}
          onKeyDown={onKeyDown}
          multiline
          displayClassName="text-sm font-medium"
        />

        <Handle
          type="source"
          position={Position.Bottom}
          style={{ background: "hsl(var(--node-input))" }}
        />
      </div>
    </div>
  )
})

InputNode.displayName = "InputNode"
export default InputNode
